import * as s from "../SignupStyled";
import * as cs from "../../SignCommonStyled";
import { checkPasswordNumberRegEx, checkPasswordRegEx } from "utils/regEx";
import ToggleComponent from "components/atoms/toggle/Toggle";
import {
  CustomError,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import useApiError from "hook/useApiError";
import useStatusMutation from "hook/useStatusMutation";
import { postRegister } from "api/authAPI";
import {
  SIGNUP_STEP,
  SIGNUP_UPDATE_TYPE,
  SignupUserInfo,
} from "api/interfaces/authInterface.interface";
import InputItem from "../common/InputItem";
import Password from "../common/Password";
import Phone from "../common/Phone";
import Agreement from "../common/Agreement";
import { useCallback } from "react";
import { Text, ValidateWord } from "components/atoms/text/Text";
import { EULAModal } from "pages/terms/text/EULAModal";
import { checkEmpty, toTrim } from "utils/functions";

interface Props {
  checkedEmail: string;
  onClickVerify: () => void;
  onClickVerifyCancel: () => void;
  onChangeUserInfo: (type: SIGNUP_UPDATE_TYPE, data: string | boolean) => void;
  userInfo: SignupUserInfo;
  verifyCode: string;
  onChangeVerifyCode: (value: string) => void;
  firstNameRef: React.RefObject<HTMLInputElement>;
  lastNameRef: React.RefObject<HTMLInputElement>;
  passwordRef: React.RefObject<HTMLInputElement>;
  confirmPasswordRef: React.RefObject<HTMLInputElement>;
  agreementRef: React.RefObject<HTMLInputElement>;
  validateMsg: string;
  onClickResend: () => void;
  isValidVerify: boolean;
  isPhoneVerified: boolean;
  onSendVerify: () => void;
  mfaNumber: string;
  validateInputMsg: ValidateMsg;
  setIsValidated: React.Dispatch<React.SetStateAction<Validate>>;
  setValidateInputMsg: React.Dispatch<React.SetStateAction<ValidateMsg>>;
  setUserInfo: React.Dispatch<React.SetStateAction<SignupUserInfo>>;
  isVerifyPhoneNumber: boolean;
  setValidateMsg: React.Dispatch<React.SetStateAction<string>>;
  setStep: React.Dispatch<React.SetStateAction<SIGNUP_STEP>>;
  setIsValidVerify: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function InputInfo(props: Props): JSX.Element {
  const {
    checkedEmail,
    onClickVerify,
    onClickVerifyCancel,
    onChangeUserInfo,
    userInfo,
    verifyCode,
    onChangeVerifyCode,
    firstNameRef,
    lastNameRef,
    passwordRef,
    confirmPasswordRef,
    agreementRef,
    validateMsg,
    onClickResend,
    isValidVerify,
    isPhoneVerified,
    onSendVerify,
    mfaNumber,
    validateInputMsg,
    setIsValidated,
    setValidateInputMsg,
    setUserInfo,
    isVerifyPhoneNumber,
    setValidateMsg,
    setStep,
    setIsValidVerify,
  } = props;
  const { intlString, lang, displayError } = useApiError();

  const mutationCreateUser = useStatusMutation(postRegister, {
    onSuccess: () => {
      if (userInfo.isTrial) {
        setStep(SIGNUP_STEP.TRIAL_SUCCESS);
      } else {
        setStep(SIGNUP_STEP.SUCCESS);
      }
    },
    onError: (err: CustomError) => {
      setIsValidVerify(true);
      setValidateMsg(
        displayError(err, intlString(lang.validateMsg.signup.fail))
      );
    },
  });
  const onClickSignup = useCallback(() => {
    setIsValidated({
      firstName: false,
      lastName: false,
      password: false,
      confirmPassword: false,
      phoneNumber: false,
      agreement: false,
    });
    setValidateInputMsg({
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
      phoneNumber: "",
      agreement: "",
    });

    const info: SignupUserInfo = {
      ...userInfo,
      firstName: toTrim(userInfo.firstName),
      lastName: toTrim(userInfo.lastName),
      password: toTrim(userInfo.password),
      confirmPassword: toTrim(userInfo.confirmPassword),
      phoneNumber: toTrim(userInfo.phoneNumber),
    };
    setUserInfo(info);
    let isChecked = false;

    if (checkEmpty(info.firstName)) {
      setIsValidated((prev) => ({
        ...prev,
        firstName: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        firstName: intlString(lang.validateMsg.signup.firstname),
      }));
      firstNameRef.current?.focus();
      isChecked = true;
    }
    if (checkEmpty(info.lastName)) {
      setIsValidated((prev) => ({
        ...prev,
        lastName: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        lastName: intlString(lang.validateMsg.signup.lastname),
      }));
      lastNameRef.current?.focus();
      isChecked = true;
    }

    if (checkEmpty(info.password)) {
      setIsValidated((prev) => ({
        ...prev,
        password: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        password: intlString(lang.validateMsg.signup.password.empty),
      }));
      passwordRef.current?.focus();
      isChecked = true;
    } else if (
      info.password.length < 8 ||
      !checkPasswordRegEx(info.password) ||
      !checkPasswordNumberRegEx(info.password)
    ) {
      setIsValidated((prev) => ({
        ...prev,
        password: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        password: intlString(lang.validateMsg.signup.reSetPassword.notRequired),
      }));
      passwordRef.current?.focus();
      isChecked = true;
    }

    if (checkEmpty(info.confirmPassword)) {
      setIsValidated((prev) => ({
        ...prev,
        confirmPassword: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        confirmPassword: intlString(lang.validateMsg.signup.password.empty),
      }));
      confirmPasswordRef.current?.focus();
      isChecked = true;
    } else if (info.password !== info.confirmPassword) {
      setIsValidated((prev) => ({
        ...prev,
        confirmPassword: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        confirmPassword: intlString(lang.validateMsg.signup.passwordMismatch),
      }));
      confirmPasswordRef.current?.focus();
      isChecked = true;
    }
    //핸드폰번호는 입력되었는데 인증번호 요청을 하지 않았을때
    if (!checkEmpty(info.phoneNumber) && !verifyCode) {
      setIsValidated((prev) => ({
        ...prev,
        phoneNumber: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: intlString(lang.validateMsg.signup.requiredVerifyCode),
      }));
      isChecked = true;
    }
    //인증번호 요청은 했으나 인증이 되지 않았을 때
    else if (!isVerifyPhoneNumber) {
      setIsValidated((prev) => ({
        ...prev,
        phoneNumber: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: intlString(lang.validateMsg.signup.requiredVerifyNumber),
      }));
      isChecked = true;
    }

    if (!info.agreement) {
      setIsValidated((prev) => ({
        ...prev,
        agreement: true,
      }));
      setValidateMsg(intlString(lang.validateMsg.signup.agreement));
      agreementRef.current?.focus();
      isChecked = true;
    }

    if (isChecked) {
      return;
    }

    mutationCreateUser.mutate({
      payload: {
        firstName: info.firstName,
        lastName: info.lastName,
        phone: info.phoneNumber,
        password: info.password,
        parentAccount: info.parentAccount,
        email: checkedEmail,
        use2faCode: !info.phoneNumber ? "email" : info.use2faCode,
        notifySms:
        info.use2faCode === "phone"
            ? true
            : !info.phoneNumber
            ? false
            : info.notifySms,
        isTrial: info.isTrial,
        verifyPhone: info.verifyPhone,
      },
      token: "token",
    });
  }, [userInfo, mutationCreateUser, checkedEmail]);

  return (
    <s.SignupInputWrapper>
      <s.InputWrapper>
        <InputItem
          ref={firstNameRef}
          validateInputMsg={validateInputMsg}
          onChangeUserInfo={onChangeUserInfo}
          userInfo={userInfo}
          label="First Name"
          tabIndex={1}
          fieldKey="firstName"
          maxLength={100}
          type={SIGNUP_UPDATE_TYPE.FIRST_NAME}
        />

        <InputItem
          ref={lastNameRef}
          validateInputMsg={validateInputMsg}
          onChangeUserInfo={onChangeUserInfo}
          userInfo={userInfo}
          label="Last Name"
          tabIndex={2}
          fieldKey="lastName"
          maxLength={100}
          type={SIGNUP_UPDATE_TYPE.LAST_NAME}
        />

        <Password
          ref={passwordRef}
          validateInputMsg={validateInputMsg}
          onChangeUserInfo={onChangeUserInfo}
          userInfo={userInfo}
          label="Password"
          tabIndex={3}
          fieldKey="password"
          type={SIGNUP_UPDATE_TYPE.PASSWORD}
        />

        <Password
          ref={confirmPasswordRef}
          validateInputMsg={validateInputMsg}
          onChangeUserInfo={onChangeUserInfo}
          userInfo={userInfo}
          label="Confirm Password"
          tabIndex={4}
          fieldKey="confirmPassword"
          type={SIGNUP_UPDATE_TYPE.CONFIRM_PASSWORD}
          showRules={false}
        />

        <Phone
          validateInputMsg={validateInputMsg}
          onChangeUserInfo={onChangeUserInfo}
          userInfo={userInfo}
          isPhoneVerified={isPhoneVerified}
          mfaNumber={mfaNumber}
          onClickResend={onClickResend}
          onSendVerify={onSendVerify}
          onClickVerify={onClickVerify}
          onClickVerifyCancel={onClickVerifyCancel}
          verifyCode={verifyCode}
          isValidVerify={isValidVerify}
          requiredVerify={true}
          onChangeVerifyCode={onChangeVerifyCode}
          tabIndex={5}
        >
          {isPhoneVerified && (
            <s.TFAWrapper>
              <Text fontSize={14}>2FA Code</Text>
              <div className="checkbox-wrapper ">
                <label>
                  <input
                    type="radio"
                    value="email"
                    checked={userInfo.use2faCode === "email"}
                    onChange={(e) =>
                      onChangeUserInfo(
                        SIGNUP_UPDATE_TYPE.TWOFACTOR,
                        e.target.value
                      )
                    }
                    data-testid="auth-signup-step-radio-email"
                  />
                  Email
                </label>
                <label>
                  <input
                    type="radio"
                    value="phone"
                    checked={userInfo.use2faCode === "phone"}
                    onChange={(e) =>
                      onChangeUserInfo(
                        SIGNUP_UPDATE_TYPE.TWOFACTOR,
                        e.target.value
                      )
                    }
                    disabled={
                      checkEmpty(userInfo.phoneNumber) ||
                      (!checkEmpty(userInfo.phoneNumber) && !isPhoneVerified)
                    }
                    data-testid="auth-signup-step-radio-phone"
                  />
                  Phone
                </label>
              </div>
            </s.TFAWrapper>
          )}

          {isPhoneVerified && (
            <s.TFAWrapper>
              <Text fontSize={14}>Receive SMS</Text>
              <div className="checkbox-wrapper ">
                <ToggleComponent
                  isToggled={userInfo.notifySms}
                  checked={userInfo.notifySms}
                  disabled={userInfo.use2faCode === "phone"}
                  onChange={(checked) => {
                    onChangeUserInfo(SIGNUP_UPDATE_TYPE.NOTIFYSMS, checked);
                  }}
                  dataTestId="auth-signup-step-toggle-notify-sms"
                />
              </div>
            </s.TFAWrapper>
          )}
        </Phone>
        <s.AgreementWrapperContainer>
          <Agreement
            onChangeUserInfo={onChangeUserInfo}
            userInfo={userInfo}
            termsContent={<EULAModal />}
            fieldKey="agreement"
            type={SIGNUP_UPDATE_TYPE.AGREEMENT}
            ref={agreementRef}
          />
        </s.AgreementWrapperContainer>
      </s.InputWrapper>
      <cs.Bottom>
        {validateMsg !== "" && (
          <ValidateWord data-testid="auth-signup-validate">
            {validateMsg}
          </ValidateWord>
        )}
      </cs.Bottom>
      <s.SubmitWrapper>
        {/* <cs.ValidateWrapper>
                        <ValidateWord>{validateMsg}</ValidateWord>
                      </cs.ValidateWrapper> */}
        <cs.BigButton
          id="dw_signup_button_signup"
          onClick={onClickSignup}
          data-testid="auth-signup-step-button-signup-click-signup"
        >
          Sign Up
        </cs.BigButton>
      </s.SubmitWrapper>
    </s.SignupInputWrapper>
  );
}
